import {
  IServiceLayoutItem,
  IServiceItem,
  IBooking,
  IDiarySchedule,
  PaymentTypes,
  IDiaryService,
} from 'shared-types/index';
import DateUtilsService from 'external-services/date-utils-service/index';

const NS = 'ServiceDetectionService';

class ServiceDetectionService {
  public serviceIsPaidOrBookingOptions(service: IServiceItem): boolean {
    const hasPayment =
      service.paymentDetails && service.paymentDetails.paymentType !== PaymentTypes.NoPayment;
    const hasAdditional =
      service.additionalPaymentDetails && service.additionalPaymentDetails.length > 0;

    return !!(hasPayment || hasAdditional);
  }

  /**
   * If result is false it means that the booking comes from a service via a drag or swap
   * and has retained the serviceId of its previous service, despite it being moved to a table in a different section.
   * This is a special case scenario for dragging/swapping bookings within the timeline (see NBI-2004 or confluence
   * https://nowbookit.atlassian.net/wiki/spaces/ND/pages/127107079/Logic+for+Move+tables for more details) and we
   * don't want to apply any service selection logic here, since it has already been handled in bookingDrag.service.
   * @param serviceId - serviceId from booking
   * @param sectionId - preferredSectionId from booking
   * @param serviceLayouts - serviceLayouts from schedule
   */
  public hasSectionsInService(
    serviceId: string,
    sectionId: string,
    serviceLayouts: IServiceLayoutItem[]
  ): boolean {
    return (
      serviceLayouts &&
      serviceLayouts
        .filter((sl) => {
          return sl.serviceId === serviceId;
        })
        .some((sl) => {
          return sl.layoutId && sl.sectionId === sectionId;
        })
    );
  }

  public getBookingService(booking: IBooking, schedule: IDiarySchedule): IDiaryService {
    const matchedServiceId: string = this.bookingInService(booking, schedule);
    if (matchedServiceId) {
      for (let i = schedule.services.length - 1; i >= 0; i--) {
        const serviceItem: IDiaryService = schedule.services[i];
        if (matchedServiceId === serviceItem._id) {
          return serviceItem;
        }
      }
    }
    return null; //Horita: Modified as null from false
  }

  /**
   *
   * @param booking
   * @param schedule
   * @returns {boolean|*}
   */
  public bookingInService(booking: IBooking, schedule: IDiarySchedule): string {
    const hasSectionsInService: boolean = this.hasSectionsInService(
      booking.serviceId,
      booking.preferredSectionId,
      schedule.serviceLayouts
    );

    /**
     * First does a check to see if the selected service actually has the selected section in it.
     * If not, assumes this is a "sticky" service that came from a dragged booking.
     */
    if (!hasSectionsInService) {
      return booking.serviceId;
    }

    const originalServiceId: string = booking.serviceId;
    const serviceIdInTimeRange: string = this.getNewServiceIdIfOutOfTimeRange(
      booking,
      schedule.serviceLayouts
    );

    // if serviceId was not in the time range we check if it was meant to be a "sticky" service and, if so, return the original serviceId
    if (originalServiceId !== serviceIdInTimeRange) {
      const targetService: IServiceItem = schedule.services.find(
        (s: IServiceItem) => s._id === booking.serviceId
      );
      const sticksToOriginalService: boolean =
        targetService && this.serviceIsPaidOrBookingOptions(targetService);
      if (sticksToOriginalService) {
        return originalServiceId;
      }
    }

    // if not "sticky" allow serviceId to be changed to a service that matches the time range
    return serviceIdInTimeRange; //  || false
  }

  /**
   * Checks if a booking
   * @param booking{IBooking} -
   * @param serviceLayouts{IServiceLayoutItem[]} -
   * @returns {string}
   */
  private getNewServiceIdIfOutOfTimeRange(
    booking: IBooking,
    serviceLayouts: IServiceLayoutItem[]
  ): string {
    const bookingTime: Date = booking.time;
    const serviceIdMatches: string[] = [];
    let validOriginalService: string; // validOriginalService takes precedence over serviceIdMatches

    serviceLayouts.some((serviceLayout: IServiceLayoutItem) => {
      /**
       * Horita
       * To do
       * Should be test, because serviceLayout.startTime&endTime is string
       */
      if (
        bookingTime >= DateUtilsService.getSanitizedDate(serviceLayout.startTime) &&
        bookingTime <= DateUtilsService.getSanitizedDate(serviceLayout.endTime)
      ) {
        if (booking.serviceId === serviceLayout.serviceId) {
          // the booking is during this time
          validOriginalService = booking.serviceId;
          return true; // found what we need, now break the loop
        } else if (serviceIdMatches.indexOf(serviceLayout.serviceId) === -1) {
          serviceIdMatches.push(serviceLayout.serviceId);
        }
      }
      return false;
    });

    if (validOriginalService) {
      return validOriginalService;
    }

    if (serviceIdMatches.length === 1) {
      // their is only one other service it could be
      return serviceIdMatches[0];
    }

    return '';
  }
}
const instance = new ServiceDetectionService();
export default instance as ServiceDetectionService;
